import { apiClient } from './api-client';

export interface DashboardStats {
  customers: number;
  equipment: number;
  activeIntakes: number;
  serviceLocations: number;
}

export interface InspectionQueueItem {
  id: string;
  inspectionNumber: string;
  status: string;
  operationsReviewStatus: string | null;
  commercialReviewStatus: string | null;
  customerApprovalStatus: string | null;
  customerName: string | null;
  /** Customer ORGANIZATION name — authoritative customer identity. */
  customerOrganizationName: string | null;
  equipmentName: string | null;
  engineerName: string | null;
  scheduledAt: string | null;
  completedAt: string | null;
  operationsReviewedAt: string | null;
  totalFindingsCount: number;
  priorityName?: string | null;
  priorityColor?: string | null;
}

export interface LifecycleStageCount {
  status: string;
  count: number;
}

export interface RecentActivityItem {
  /**
   * B6.17 — absent means INSPECTION. Only the Operations dashboard merges two
   * streams; every other dashboard sends a single-source inspection feed and
   * omits this field. Consumers must default it.
   */
  source?: 'INSPECTION' | 'REPORT';
  inspectionId: string;
  inspectionNumber: string;
  status: string;
  updatedAt: string;
  customerName: string | null;
  /** Customer ORGANIZATION name — authoritative customer identity. */
  customerOrganizationName: string | null;
  equipmentName: string | null;
  reportNumber?: string | null;
  actorName?: string | null;
}

/** B6.17 — one Service Report awaiting the OPS decision. Metadata only. */
export interface ReportReviewQueueItem {
  id: string;
  inspectionId: string;
  reportNumber: string | null;
  inspectionNumber: string;
  status: string;
  customerName: string | null;
  /** Customer ORGANIZATION name — authoritative customer identity. */
  customerOrganizationName: string | null;
  equipmentName: string | null;
  submittedByName: string | null;
  submittedAt: string | null;
}

export interface QuickAction {
  label: string;
  route: string;
  icon: string;
  permission: string | null;
}

export interface OwnerDashboard {
  kpis: {
    totalCustomers: number;
    totalEquipment: number;
    openIntakes: number;
    activeInspections: number;
    engineersActiveToday: number;
    pendingOpsReviews: number;
    approvedForCommercial: number;
    pendingCustomerApprovals: number;
    overdueItems: number;
    openWorkOrders: number;
    pendingDispatches: number;
    unassignedInspections: number;
  };
  queues: {
    unassigned: InspectionQueueItem[];
    overdueScheduled: InspectionQueueItem[];
    awaitingOpsReview: InspectionQueueItem[];
  };
  recentActivity: RecentActivityItem[];
  quickActions: QuickAction[];
}

export interface AdminDashboard {
  kpis: {
    unassignedInspections: number;
    /**
     * D-2 — the API sends `inProgressInspections`, NOT `inspectionsInProgress`.
     *
     * This interface previously declared the latter, copied from
     * `EngineerDashboard` below, where that name IS correct. The two dashboards
     * genuinely differ: `AdminKpisDto` declares `inProgressInspections` and
     * `EngineerKpisDto` declares `inspectionsInProgress`. Because these
     * interfaces are hand-written rather than generated from the DTOs, the
     * divergence type-checked cleanly and the value arrived `undefined` —
     * which `KpiCard` renders as `0` via `value ?? 0`, so the card read a
     * plausible zero forever instead of failing loudly.
     *
     * The backend is the contract of record and was NOT changed.
     */
    inProgressInspections: number;
    awaitingOpsReview: number;
    returnedToEngineers: number;
    overdueItems: number;
    openApprovals: number;
    openDispatches: number;
  };
  queues: {
    unassigned: InspectionQueueItem[];
    overdueScheduled: InspectionQueueItem[];
    returnedToEngineers: InspectionQueueItem[];
  };
  recentActivity: RecentActivityItem[];
  quickActions: QuickAction[];
}

export interface OperationsDashboard {
  kpis: {
    todaysIntakeCount: number;
    waitingForEngineerAssignment: number;
    inspectionInProgress: number;
    pendingOpsReviews: number;
    returnedToEngineer: number;
    waitingForCommercial: number;
    pendingCustomerDecisions: number;
    completedToday: number;
    /** B6.17 — same predicate as `pendingReportReviews` below; cannot disagree. */
    pendingReportReviews: number;
    closedJobs: number;
    cancelledJobs: number;
    /**
     * Request Thread Stage B — open modification requests awaiting an
     * Operations feasibility verdict. Counted from the same predicate the work
     * queue filters with, so tile and list cannot disagree.
     */
    modificationRequestsAwaitingOperations: number;
  };
  /** B6.17 — the primary OPS queue (DEC-1/DEC-3). */
  pendingReportReviews: ReportReviewQueueItem[];
  pendingReviews: InspectionQueueItem[];
  jobsByLifecycleStage: LifecycleStageCount[];
  recentActivity: RecentActivityItem[];
  quickActions: QuickAction[];
}

/**
 * D-131 M6 — one open job-level modification round in the Commercial queue
 * (`ModificationRoundQueueItemDto`). Ids, names, state and age only: the
 * customer's words stay on the job behind `inspection:view-commercial`.
 */
export interface ModificationRoundQueueItem {
  roundId: string;
  inspectionId: string;
  inspectionNumber: string;
  roundNumber: number;
  state: string;
  currentOwner: string | null;
  waitingFor: string | null;
  classification: string | null;
  customerName: string | null;
  customerOrganizationName: string | null;
  equipmentName: string | null;
  assigneeName: string | null;
  requestedAt: string;
  updatedAt: string;
  ageDays: number;
}

export interface CommercialDashboard {
  kpis: {
    awaitingCommercialReview: number;
    quotationsPendingUpload: number;
    commercialPackagesCompleted: number;
    returnedToOperations: number;
    averageTurnaroundDays: number;
  };
  queues: {
    reviewQueue: InspectionQueueItem[];
    awaitingCustomerResponse: InspectionQueueItem[];
    /** Legacy-column derivation, retained by the API until M9. */
    returnedFromCustomer: InspectionQueueItem[];
    /** D-131 M6 — open rounds, oldest request first (capped top-N). */
    modificationRounds?: ModificationRoundQueueItem[];
    openModificationRounds?: number;
  };
  recentActivity: RecentActivityItem[];
  quickActions: QuickAction[];
}

export interface RequestCenterCounts {
  inspectionRequests: number;
  transferRequests: number;
  takeoverRequests: number;
  authorizationRequests: number;
  pendingReviews: number;
  customerApprovals: number;
  rejectedItems: number;
}

export interface EngineerDashboard {
  kpis: {
    assignedInspections: number;
    inspectionsInProgress: number;
    completedToday: number;
    returnedToMe: number;
    tasksPending: number;
    assignedWorkOrders: number;
  };
  queues: {
    myQueue: InspectionQueueItem[];
    returnedInspections: InspectionQueueItem[];
  };
  recentActivity: RecentActivityItem[];
  quickActions: QuickAction[];
  requestCenter: RequestCenterCounts;
}

export interface MasterEngineerDashboard {
  kpis: {
    allOpenInspections: number;
    myActiveAssignments: number;
    unassignedTeamInspections: number;
    returnedInspections: number;
    escalatedCases: number;
    checklistApprovals: number;
    engineerSupportRequests: number;
  };
  queues: {
    unassigned: InspectionQueueItem[];
    myAssignments: InspectionQueueItem[];
    returnedToMe: InspectionQueueItem[];
  };
  recentActivity: RecentActivityItem[];
  quickActions: QuickAction[];
  requestCenter: RequestCenterCounts;
}

export interface EngineerWorkload {
  id: string;
  firstName: string;
  lastName: string;
  roleSlug: string;
  roleName: string;
  departmentId: string | null;
  departmentName: string | null;
  isActive: boolean;
  activeJobs: number;
  assignedJobs: number;
  pendingReviewJobs: number;
  lastAssignmentAt: string | null;
  isRecommended: boolean;
}

export interface AssignmentTeamWide {
  activeJobs: number;
  assignedJobs: number;
}

export interface AssignmentRecentActivityItem {
  inspectionId: string;
  inspectionNumber: string;
  engineerName: string | null;
  assignedAt: string;
  assignedByName: string | null;
}

export interface AssignmentCenterData {
  engineers: EngineerWorkload[];
  teamWide: AssignmentTeamWide;
  recentActivity: AssignmentRecentActivityItem[];
}

export const dashboardService = {
  getStats: (): Promise<DashboardStats> =>
    apiClient.get<DashboardStats>('/dashboard/stats').then((r) => r.data),

  getOwnerDashboard: (): Promise<OwnerDashboard> =>
    apiClient.get<OwnerDashboard>('/dashboard/owner').then((r) => r.data),

  getAdminDashboard: (): Promise<AdminDashboard> =>
    apiClient.get<AdminDashboard>('/dashboard/admin').then((r) => r.data),

  getOperationsDashboard: (): Promise<OperationsDashboard> =>
    apiClient.get<OperationsDashboard>('/dashboard/operations').then((r) => r.data),

  getAssignmentCenterData: (): Promise<AssignmentCenterData> =>
    apiClient
      .get<AssignmentCenterData>('/dashboard/operations/assignment-center')
      .then((r) => r.data),

  getCommercialDashboard: (): Promise<CommercialDashboard> =>
    apiClient.get<CommercialDashboard>('/dashboard/commercial').then((r) => r.data),

  getEngineerDashboard: (): Promise<EngineerDashboard> =>
    apiClient.get<EngineerDashboard>('/dashboard/engineer').then((r) => r.data),

  getMasterEngineerDashboard: (): Promise<MasterEngineerDashboard> =>
    apiClient.get<MasterEngineerDashboard>('/dashboard/master-engineer').then((r) => r.data),
};
